import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Reveal, SectionTag } from "./Reveal";
import { ENQUIRY_OPTIONS } from "@/data/products";
import { INSTAGRAM_URL } from "@/data/assets";
import { api, errMsg } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Send, Instagram, MapPin } from "lucide-react";

const field =
  "w-full bg-[#0e0e0e] border border-white/12 px-4 py-3.5 text-white placeholder:text-white/30 text-sm outline-none focus:border-[#e31e24] transition-colors";

export const Contact = ({ selectedPart, setSelectedPart, partNonce }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [part, setPart] = useState(selectedPart || "");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setPart(selectedPart || "");
    // partNonce forces re-prefill even when the same product is re-selected
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPart, partNonce]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSending(true);
    try {
      await api.createEnquiry({
        name: form.name,
        email: form.email,
        phone: form.phone,
        part: part,
        message: form.message,
      });
      toast.success("Enquiry sent — we'll be in touch shortly.", {
        description: `${form.name} · ${part || "General enquiry"}`,
      });
      setForm({ name: "", email: "", phone: "", message: "" });
      setPart("");
      setSelectedPart("");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative py-24 md:py-36 px-5 md:px-10 overflow-hidden"
      data-testid="contact-section"
    >
      <div className="absolute inset-0 rp-red-glow opacity-60 pointer-events-none" />
      <div className="relative mx-auto max-w-[1400px] grid md:grid-cols-[0.9fr_1.1fr] gap-14">
        <div>
          <SectionTag index="06" label="Enquire" />
          <h2 className="font-display text-[clamp(2.6rem,7vw,6rem)] text-white mb-6">
            Get a <span className="text-[#e31e24]">quote</span>.
          </h2>
          <p className="text-white/60 text-base leading-relaxed max-w-md mb-10">
            Tell us the part and the car. We'll confirm spec, price and shipping
            before anything gets printed.
          </p>

          <div className="space-y-4">
            <div className="flex items-center gap-3 text-white/70">
              <span className="grid place-items-center h-9 w-9 border border-white/15 text-[#e31e24]">
                <MapPin size={16} />
              </span>
              <span className="text-sm">Based in Brisbane, Australia</span>
            </div>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-3 text-white/70 hover:text-white transition-colors w-max"
              data-testid="contact-instagram"
            >
              <span className="grid place-items-center h-9 w-9 border border-white/15 text-[#e31e24]">
                <Instagram size={16} />
              </span>
              <span className="text-sm">@rayce_parts</span>
            </a>
          </div>
        </div>

        <Reveal delay={0.1}>
          <form
            onSubmit={submit}
            noValidate
            className="bg-[#141414] border border-white/10 p-6 md:p-9"
            data-testid="enquiry-form"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="font-mono-rp text-[11px] tracking-[0.2em] uppercase text-white/50 mb-2 block">
                  Name *
                </label>
                <input
                  className={field}
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your name"
                  data-testid="form-name"
                />
              </div>
              <div>
                <label className="font-mono-rp text-[11px] tracking-[0.2em] uppercase text-white/50 mb-2 block">
                  Email *
                </label>
                <input
                  type="email"
                  className={field}
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@email.com"
                  data-testid="form-email"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="font-mono-rp text-[11px] tracking-[0.2em] uppercase text-white/50 mb-2 block">
                  Phone (optional)
                </label>
                <input
                  className={field}
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="04xx xxx xxx"
                  data-testid="form-phone"
                />
              </div>
              <div>
                <label className="font-mono-rp text-[11px] tracking-[0.2em] uppercase text-white/50 mb-2 block">
                  Part of Interest
                </label>
                <Select value={part} onValueChange={setPart}>
                  <SelectTrigger
                    className="w-full bg-[#0e0e0e] border-white/12 rounded-none py-3.5 h-auto text-white focus:border-[#e31e24] focus:ring-0"
                    data-testid="form-part-select"
                  >
                    <SelectValue placeholder="Select a part" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141414] border-white/15 text-white rounded-none">
                    {ENQUIRY_OPTIONS.map((o) => (
                      <SelectItem
                        key={o}
                        value={o}
                        className="focus:bg-[#e31e24] focus:text-white rounded-none"
                        data-testid={`part-option-${o}`}
                      >
                        {o}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-4">
              <label className="font-mono-rp text-[11px] tracking-[0.2em] uppercase text-white/50 mb-2 block">
                Message
              </label>
              <textarea
                rows={4}
                className={field + " resize-none"}
                value={form.message}
                onChange={update("message")}
                placeholder="Tell us about your car and what you need…"
                data-testid="form-message"
              />
            </div>

            <button
              type="submit"
              disabled={sending}
              className="btn-race w-full justify-center mt-6 disabled:opacity-60"
              data-testid="form-submit"
            >
              {sending ? "Sending…" : "Send Enquiry"} <Send size={16} />
            </button>
            <p className="mt-3 font-mono-rp text-[11px] text-white/50 text-center">
              Shipping cost confirmed with your quote.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
